import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Users, Search, Edit, Plus, Mail, Phone, MessageSquare, 
  FileText, CheckCircle, XCircle, ChevronDown, ChevronUp,
  QrCode, Key, Send, RefreshCw, Download, Trash2, UserPlus, 
  Calendar, AlertTriangle, Clock, Eye, Globe, Monitor,
  Smartphone, Tablet, Laptop, Chrome, EyeOff
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ConsentCollectionModal from '@/components/ConsentCollectionModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CONTACT_TYPES = [
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'phone', label: 'Telefone', icon: Phone },
  { id: 'sms', label: 'SMS', icon: MessageSquare },
  { id: 'whatsapp', label: 'WhatsApp', icon: MessageSquare },
  { id: 'postal', label: 'Postal', icon: FileText }
];

const RETENTION_PERIODS = [
  { id: 'event_management', value: 90, label: 'Gestão do Evento - 90 dias', description: 'Art. 6º(1)(b)' },
  { id: 'consent_1_year', value: 365, label: 'Consentimento - 1 Ano', description: 'Art. 6º(1)(a)' },
  { id: 'consent_2_years', value: 730, label: 'Consentimento - 2 Anos', description: 'Art. 6º(1)(a)' },
  { id: 'until_withdrawal', value: null, label: 'Até Retirada do Consentimento', description: 'Art. 6º(1)(a)' },
  { id: 'legal_10_years', value: 3650, label: 'Obrigação Legal - 10 Anos', description: 'Art. 6º(1)(c)' }
];

const TitularesPage = () => {
  const { user } = useOutletContext();
  const [titulares, setTitulares] = useState([]);
  const [filteredTitulares, setFilteredTitulares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedTitular, setExpandedTitular] = useState(null);
  
  // Create titular dialog
  const [createTitularDialog, setCreateTitularDialog] = useState(false);
  const [newTitularForm, setNewTitularForm] = useState({
    first_name: '',
    last_name: '',
    surname: '',
    email: '',
    phone: '',
    mobile: '',
    company_name: '',
    nif: '',
    autoline_id: '',
    address: '',
    locality: '',
    postal_code: '',
    legal_basis: 'consent' // Base legal padrão
  });
  
  // Edit titular dialog
  const [editDialog, setEditDialog] = useState({ open: false, titular: null });
  const [editForm, setEditForm] = useState({ 
    first_name: '', 
    last_name: '', 
    surname: '', 
    email: '', 
    phone: '', 
    mobile: '',
    company_name: '',
    nif: '',
    autoline_id: '',
    address: '', 
    locality: '',
    postal_code: '',
    legal_basis: 'consent',
    groups: [] 
  });
  
  // Legal basis options
  const [legalBasisOptions, setLegalBasisOptions] = useState([
    { value: 'consent', label: 'Consentimento (Art. 6º, 1, a)' },
    { value: 'contract', label: 'Execução de Contrato (Art. 6º, 1, b)' },
    { value: 'legal_obligation', label: 'Obrigação Legal (Art. 6º, 1, c)' },
    { value: 'vital_interest', label: 'Interesse Vital (Art. 6º, 1, d)' },
    { value: 'public_interest', label: 'Interesse Público (Art. 6º, 1, e)' },
    { value: 'legitimate_interest', label: 'Interesse Legítimo (Art. 6º, 1, f)' }
  ]);
  
  // Add consent dialog
  const [consentDialog, setConsentDialog] = useState({ open: false, titularId: null, titularEmail: null });
  const [consentForm, setConsentForm] = useState({
    contact_type: '',
    contact_value: '',
    purposes: [],
    validation_method: 'manual',
    retention_period_days: 365,
    retention_period_id: 'consent_1_year'
  });
  
  // Consent collection dialog (QR/OTP) - Unified modal
  const [collectDialog, setCollectDialog] = useState({ open: false, titular: null });
  const [unifiedConsentModal, setUnifiedConsentModal] = useState({ open: false, titular: null });
  const [qrImageUrl, setQrImageUrl] = useState(null);
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [validationToken, setValidationToken] = useState('');
  const [collectForm, setCollectForm] = useState({
    contact_type: '',
    contact_value: '',
    purposes: [],
    retention_period_days: 365,
    retention_period_id: 'consent_1_year'
  });
  
  // Edit consent dialog
  const [editConsentDialog, setEditConsentDialog] = useState({ open: false, titular: null, consent: null });
  const [editConsentForm, setEditConsentForm] = useState({
    contact_value: '',
    purposes: [],
    status: ''
  });
  
  // RTBF dialog
  const [rtbfDialog, setRtbfDialog] = useState({ open: false, titular: null });
  const [rtbfForm, setRtbfForm] = useState({
    execution_type: 'delayed',
    identity_verified: false,
    reason: '',
    notes: ''
  });
  
  // Evidence dialog
  const [evidenceDialog, setEvidenceDialog] = useState({ open: false, evidence: null, loading: false });
  
  // Consent purposes from API
  const [consentPurposes, setConsentPurposes] = useState([]);
  
  // Groups
  const [groups, setGroups] = useState([]);
  
  // RTBF visibility toggle
  const [showRtbfData, setShowRtbfData] = useState(false);
  
  // Tenant filter
  const [tenants, setTenants] = useState([]);
  const [tenantFilter, setTenantFilter] = useState('all');

  useEffect(() => {
    fetchTitulares();
    fetchGroups();
    fetchConsentPurposes();
  }, [showRtbfData, tenantFilter]);

  useEffect(() => {
    filterTitulares();
  }, [titulares, searchTerm]);

  const fetchConsentPurposes = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/consent-purposes/active`, { withCredentials: true });
      setConsentPurposes(response.data || []);
    } catch (error) {
      console.error('Error fetching consent purposes:', error);
      // Fallback
      setConsentPurposes([
        { purpose_id: 'newsletter', name: 'Newsletter' },
        { purpose_id: 'marketing', name: 'Marketing' },
        { purpose_id: 'profiling', name: 'Profiling' },
        { purpose_id: 'partners', name: 'Parceiros' }
      ]);
    }
  };

  const fetchTitulares = async () => {
    try {
      let url = `${BACKEND_URL}/api/titulares?include_rtbf=${showRtbfData}`;
      if (tenantFilter && tenantFilter !== 'all') {
        url += `&tenant_filter=${tenantFilter}`;
      }
      const response = await axios.get(url, { withCredentials: true });
      
      // Handle new response format with tenants list
      if (response.data.titulares) {
        setTitulares(response.data.titulares);
        setTenants(response.data.tenants || []);
      } else {
        setTitulares(response.data);
      }
    } catch (error) {
      console.error('Error fetching titulares:', error);
      toast.error('Erro ao carregar titulares');
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/titular-groups`, { withCredentials: true });
      setGroups(response.data);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const filterTitulares = () => {
    if (!searchTerm) {
      setFilteredTitulares(titulares);
      return;
    }
    
    const filtered = titulares.filter(t => 
      t.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredTitulares(filtered);
  };

  // === CREATE TITULAR ===
  const handleCreateTitular = async () => {
    if (!newTitularForm.first_name || !newTitularForm.email) {
      toast.error('Primeiro nome e email são obrigatórios');
      return;
    }
    
    if (!newTitularForm.legal_basis) {
      toast.error('Base legal é obrigatória');
      return;
    }
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/titulares`,
        newTitularForm,
        { withCredentials: true }
      );
      toast.success('Titular criado com sucesso');
      setCreateTitularDialog(false);
      
      const selectedLegalBasis = newTitularForm.legal_basis;
      
      setNewTitularForm({ 
        first_name: '', 
        last_name: '', 
        surname: '', 
        email: '', 
        phone: '', 
        mobile: '',
        company_name: '',
        nif: '',
        autoline_id: '',
        address: '', 
        locality: '',
        postal_code: '',
        legal_basis: 'consent'
      });
      await fetchTitulares();
      
      // Only open consent collection if legal basis is 'consent'
      const newTitular = response.data;
      if (newTitular && newTitular.user_id && selectedLegalBasis === 'consent') {
        setUnifiedConsentModal({ 
          open: true, 
          titular: {
            ...newTitular,
            contacts: { email: [newTitular.email], phone: newTitular.phone ? [newTitular.phone] : [], mobile: newTitular.mobile ? [newTitular.mobile] : [] },
            purposes: []
          }
        });
      }
    } catch (error) {
      console.error('Error creating titular:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar titular');
    }
  };

  // === RTBF - Right To Be Forgotten ===
  const handleOpenRTBF = (titular) => {
    setRtbfForm({
      execution_type: 'delayed',
      identity_verified: false,
      reason: '',
      notes: ''
    });
    setRtbfDialog({ open: true, titular });
  };

  const handleSubmitRTBF = async () => {
    if (!rtbfForm.identity_verified) {
      toast.error('Deve confirmar a validação de identidade do titular');
      return;
    }
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/rtbf/request`,
        {
          user_id: rtbfDialog.titular.user_id,
          ...rtbfForm
        },
        { withCredentials: true }
      );
      
      if (rtbfForm.execution_type === 'immediate') {
        toast.success('Direito ao Esquecimento executado. Dados apagados.');
      } else {
        toast.success(`Direito ao Esquecimento agendado para ${new Date(response.data.execution_date).toLocaleDateString('pt-PT')}`);
      }
      
      setRtbfDialog({ open: false, titular: null });
      fetchTitulares();
    } catch (error) {
      console.error('Error submitting RTBF:', error);
      toast.error(error.response?.data?.detail || 'Erro ao processar pedido RTBF');
    }
  };

  const handleEditTitular = (titular) => {
    setEditForm({ 
      name: titular.name, 
      email: titular.email,
      phone: titular.phone || '',
      address: titular.address || '',
      legal_basis: titular.legal_basis || 'consent',
      groups: titular.groups || []
    });
    setEditDialog({ open: true, titular });
  };

  const handleSaveTitular = async () => {
    try {
      await axios.patch(
        `${BACKEND_URL}/api/titulares/${editDialog.titular.user_id}`,
        editForm,
        { withCredentials: true }
      );
      toast.success('Titular atualizado com sucesso');
      setEditDialog({ open: false, titular: null });
      fetchTitulares();
      fetchGroups();
    } catch (error) {
      console.error('Error updating titular:', error);
      toast.error(error.response?.data?.detail || 'Erro ao atualizar titular');
    }
  };

  const handleAddConsent = (titular) => {
    // Pre-fill with titular's email
    setConsentForm({ 
      contact_type: 'email', 
      contact_value: titular.email || '', 
      purposes: [], 
      validation_method: 'manual',
      retention_period_days: 365
    });
    setConsentDialog({ open: true, titularId: titular.user_id, titularEmail: titular.email });
  };

  const handleSaveNewConsent = async () => {
    if (!consentForm.contact_type || !consentForm.contact_value || consentForm.purposes.length === 0) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/titulares/${consentDialog.titularId}/consents`,
        consentForm,
        { withCredentials: true }
      );
      toast.success('Consentimento adicionado com sucesso');
      setConsentDialog({ open: false, titularId: null, titularEmail: null });
      fetchTitulares();
    } catch (error) {
      console.error('Error adding consent:', error);
      toast.error(error.response?.data?.detail || 'Erro ao adicionar consentimento');
    }
  };

  // === Consent Collection via QR/OTP ===
  const handleCollectConsent = (titular) => {
    // Use the unified ConsentCollectionModal with pre-filled data
    setUnifiedConsentModal({ open: true, titular });
  };

  // Handler for successful consent collection via unified modal
  const handleUnifiedConsentSuccess = (consent) => {
    toast.success('Consentimento recolhido com sucesso!');
    setUnifiedConsentModal({ open: false, titular: null });
    fetchTitulares();
  };

  // Legacy function for manual QR generation (kept for OTP tab)
  const handleCollectConsentLegacy = (titular) => {
    // Pre-fill form with titular's data
    const prefillContactType = titular.contacts?.email?.length > 0 ? 'email' : 
                               titular.contacts?.phone?.length > 0 ? 'phone' : 
                               titular.contacts?.sms?.length > 0 ? 'sms' : 'email';
    const prefillContactValue = titular.contacts?.email?.[0] || 
                                titular.contacts?.phone?.[0] || 
                                titular.contacts?.sms?.[0] || 
                                titular.email || '';
    
    setCollectForm({ 
      contact_type: prefillContactType, 
      contact_value: prefillContactValue, 
      purposes: titular.purposes || [],
      retention_period_days: 365
    });
    setQrImageUrl(null);
    setOtpCode('');
    setOtpSent(false);
    setValidationToken('');
    setCollectDialog({ open: true, titular });
  };

  const handleGenerateQR = async () => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/qr/generate`,
        {},
        { withCredentials: true, responseType: 'blob' }
      );
      
      const imageUrl = URL.createObjectURL(response.data);
      setQrImageUrl(imageUrl);
      toast.success('QR Code gerado com sucesso!');
    } catch (error) {
      console.error('Error generating QR:', error);
      toast.error('Erro ao gerar QR Code');
    }
  };

  const handleSendOTP = async () => {
    if (!collectDialog.titular?.email) {
      toast.error('Titular não tem email registado');
      return;
    }
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/otp/request`,
        { email: collectDialog.titular.email, purpose: 'consent_collection' },
        { withCredentials: true }
      );
      setOtpSent(true);
      toast.success('Código OTP enviado para ' + collectDialog.titular.email);
    } catch (error) {
      console.error('Error sending OTP:', error);
      toast.error(error.response?.data?.detail || 'Erro ao enviar OTP');
    }
  };

  const handleVerifyOTP = async () => {
    if (!otpCode || otpCode.length !== 6) {
      toast.error('Introduza o código OTP de 6 dígitos');
      return;
    }
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/otp/verify`,
        { email: collectDialog.titular.email, otp_code: otpCode },
        { withCredentials: true }
      );
      setValidationToken(response.data.validation_token);
      toast.success('Código OTP validado!');
    } catch (error) {
      console.error('Error verifying OTP:', error);
      toast.error(error.response?.data?.detail || 'Código OTP inválido');
    }
  };

  const handleSaveCollectedConsent = async () => {
    if (!collectForm.contact_type || !collectForm.contact_value || collectForm.purposes.length === 0) {
      toast.error('Preencha todos os campos do consentimento');
      return;
    }
    
    if (!validationToken && !qrImageUrl) {
      toast.error('Valide a identidade primeiro (QR Code ou OTP)');
      return;
    }
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/titulares/${collectDialog.titular.user_id}/consents`,
        {
          contact_type: collectForm.contact_type,
          contact_value: collectForm.contact_value,
          purposes: collectForm.purposes,
          retention_period_days: collectForm.retention_period_days,
          retention_period_id: collectForm.retention_period_id,
          validation_method: validationToken ? 'otp' : 'qr',
          validation_token: validationToken
        },
        { withCredentials: true }
      );
      toast.success('Consentimento recolhido com sucesso!');
      setCollectDialog({ open: false, titular: null });
      fetchTitulares();
    } catch (error) {
      console.error('Error saving consent:', error);
      toast.error(error.response?.data?.detail || 'Erro ao guardar consentimento');
    }
  };

  const handleEditConsent = (titular, consent) => {
    setEditConsentForm({
      contact_value: consent.contact_value,
      purposes: consent.purposes || [],
      status: consent.status
    });
    setEditConsentDialog({ open: true, titular, consent });
  };

  const handleSaveConsent = async () => {
    try {
      await axios.patch(
        `${BACKEND_URL}/api/titulares/${editConsentDialog.titular.user_id}/consents/${editConsentDialog.consent.consent_id}`,
        editConsentForm,
        { withCredentials: true }
      );
      toast.success('Consentimento atualizado com sucesso');
      setEditConsentDialog({ open: false, titular: null, consent: null });
      fetchTitulares();
    } catch (error) {
      console.error('Error updating consent:', error);
      toast.error(error.response?.data?.detail || 'Erro ao atualizar consentimento');
    }
  };

  const handleViewEvidence = async (consentId) => {
    setEvidenceDialog({ open: true, evidence: null, loading: true });
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/consents/${consentId}/evidence`,
        { withCredentials: true }
      );
      setEvidenceDialog({ open: true, evidence: response.data, loading: false });
    } catch (error) {
      console.error('Error fetching evidence:', error);
      toast.error('Erro ao carregar evidência');
      setEvidenceDialog({ open: false, evidence: null, loading: false });
    }
  };

  const togglePurpose = (purposeId, form, setForm) => {
    const current = form.purposes || [];
    if (current.includes(purposeId)) {
      setForm({ ...form, purposes: current.filter(p => p !== purposeId) });
    } else {
      setForm({ ...form, purposes: [...current, purposeId] });
    }
  };

  const getContactIcon = (type) => {
    const contactType = CONTACT_TYPES.find(c => c.id === type);
    return contactType ? contactType.icon : Mail;
  };

  const getPurposeLabel = (id) => {
    const purpose = consentPurposes.find(p => p.purpose_id === id);
    return purpose ? purpose.name : id;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#10B981]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="titulares-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Titulares dos Dados
          </h1>
          <p className="text-slate-600">
            Gestão de titulares, contactos e recolha de consentimentos
          </p>
        </div>
        <Button 
          onClick={() => setCreateTitularDialog(true)}
          className="bg-[#10B981] hover:bg-[#10B981]/90"
          data-testid="create-titular-button"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Novo Titular
        </Button>
      </div>

      {/* Search */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="search-titulares-input"
            placeholder="Pesquisar por nome ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <div className="flex items-center gap-3 flex-wrap">
          {/* Tenant Filter - Only for Super Admin */}
          {tenants.length > 0 && (
            <Select value={tenantFilter} onValueChange={setTenantFilter}>
              <SelectTrigger className="w-48" data-testid="tenant-filter-select">
                <SelectValue placeholder="Filtrar por Tenant" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Tenants</SelectItem>
                {tenants.map(t => (
                  <SelectItem key={t.tenant_id || 'master'} value={t.tenant_id || 'master'}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          {/* RTBF Visibility Toggle */}
          <div className="flex items-center gap-3 p-3 bg-slate-100 rounded-lg border border-slate-200">
            <div className="flex items-center gap-2">
              {showRtbfData ? (
                <Eye className="w-4 h-4 text-amber-600" />
              ) : (
                <EyeOff className="w-4 h-4 text-slate-500" />
              )}
              <Label htmlFor="rtbf-toggle" className="text-sm font-medium cursor-pointer">
                Mostrar dados RTBF
              </Label>
            </div>
            <Switch
              id="rtbf-toggle"
              data-testid="rtbf-toggle"
              checked={showRtbfData}
              onCheckedChange={setShowRtbfData}
            />
            {showRtbfData && (
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 text-xs">
                A mostrar dados anonimizados
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="pt-4 text-center">
            <div className="text-3xl font-bold text-[#0F172A]">{titulares.length}</div>
            <p className="text-sm text-slate-600">Total Titulares</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-4 text-center">
            <div className="text-3xl font-bold text-emerald-600">
              {titulares.reduce((acc, t) => acc + (t.active_consents_count || 0), 0)}
            </div>
            <p className="text-sm text-slate-600">Consentimentos Ativos</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-4 text-center">
            <div className="text-3xl font-bold text-blue-600">
              {titulares.filter(t => t.contacts?.email?.length > 0).length}
            </div>
            <p className="text-sm text-slate-600">Com Email</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-4 text-center">
            <div className="text-3xl font-bold text-purple-600">
              {titulares.filter(t => t.contacts?.phone?.length > 0 || t.contacts?.sms?.length > 0).length}
            </div>
            <p className="text-sm text-slate-600">Com Telefone</p>
          </CardContent>
        </Card>
      </div>

      {/* Titulares Table */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Lista de Titulares ({filteredTitulares.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredTitulares.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>Nenhum titular encontrado</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredTitulares.map((titular) => (
                <div key={titular.user_id} className="border rounded-lg overflow-hidden">
                  {/* Main Row */}
                  <div 
                    className="flex items-center justify-between p-4 bg-white hover:bg-slate-50 cursor-pointer"
                    onClick={() => setExpandedTitular(expandedTitular === titular.user_id ? null : titular.user_id)}
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                        <Users className="w-5 h-5 text-slate-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-[#0F172A] truncate">{titular.name}</h3>
                        <p className="text-sm text-slate-500 truncate">{titular.email}</p>
                        {titular.tenant_name && (
                          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200 mt-1">
                            {titular.tenant_name}
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    {/* Contact Icons */}
                    <div className="flex items-center gap-2 px-4">
                      {CONTACT_TYPES.map(ct => {
                        const hasContact = titular.contacts?.[ct.id]?.length > 0;
                        return (
                          <div 
                            key={ct.id}
                            className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              hasContact ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'
                            }`}
                            title={ct.label}
                          >
                            <ct.icon className="w-4 h-4" />
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Purposes */}
                    <div className="flex items-center gap-1 px-4 min-w-[200px]">
                      {titular.purposes?.slice(0, 3).map(p => (
                        <Badge key={p} variant="outline" className="text-xs">
                          {getPurposeLabel(p)}
                        </Badge>
                      ))}
                      {titular.purposes?.length > 3 && (
                        <Badge variant="outline" className="text-xs">+{titular.purposes.length - 3}</Badge>
                      )}
                    </div>
                    
                    {/* Stats */}
                    <div className="flex items-center gap-4 px-4">
                      <div className="text-center">
                        <div className="text-lg font-semibold text-emerald-600">{titular.active_consents_count || 0}</div>
                        <div className="text-xs text-slate-500">Ativos</div>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleCollectConsent(titular); }}
                        className="bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                        title="Recolher consentimento via QR/OTP"
                        data-testid={`collect-consent-${titular.user_id}`}
                      >
                        <QrCode className="w-4 h-4 mr-1" />
                        Recolher
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleEditTitular(titular); }}
                        data-testid={`edit-titular-${titular.user_id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleAddConsent(titular); }}
                        data-testid={`add-consent-${titular.user_id}`}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleOpenRTBF(titular); }}
                        className="bg-red-50 border-red-200 text-red-700 hover:bg-red-100"
                        title="Direito ao Esquecimento (Art. 17º RGPD)"
                        data-testid={`rtbf-${titular.user_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      {expandedTitular === titular.user_id ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </div>
                  
                  {/* Expanded Content - Consents */}
                  {expandedTitular === titular.user_id && (
                    <div className="border-t bg-slate-50 p-4">
                      <h4 className="font-semibold text-[#0F172A] mb-3">Consentimentos</h4>
                      {titular.consents?.length === 0 ? (
                        <p className="text-sm text-slate-500">Nenhum consentimento registado</p>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Tipo</TableHead>
                              <TableHead>Contacto</TableHead>
                              <TableHead>Finalidades</TableHead>
                              <TableHead>Estado</TableHead>
                              <TableHead>Validação</TableHead>
                              <TableHead>Data</TableHead>
                              <TableHead>Expira</TableHead>
                              <TableHead className="text-right">Ações</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {titular.consents?.map((consent) => {
                              const ContactIcon = getContactIcon(consent.contact_type);
                              const isExpired = consent.expiration_date && new Date(consent.expiration_date) < new Date();
                              const isExpiringSoon = consent.expiration_date && 
                                new Date(consent.expiration_date) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) &&
                                new Date(consent.expiration_date) > new Date();
                              return (
                                <TableRow key={consent.consent_id} className={isExpired ? 'bg-red-50' : isExpiringSoon ? 'bg-amber-50' : ''}>
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      <ContactIcon className="w-4 h-4 text-slate-500" />
                                      <span className="capitalize">{consent.contact_type}</span>
                                    </div>
                                  </TableCell>
                                  <TableCell className="font-mono text-sm">{consent.contact_value}</TableCell>
                                  <TableCell>
                                    <div className="flex flex-wrap gap-1">
                                      {consent.purposes?.map(p => (
                                        <Badge key={p} variant="outline" className="text-xs">
                                          {getPurposeLabel(p)}
                                        </Badge>
                                      ))}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    {consent.status === 'active' && !isExpired ? (
                                      <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
                                        <CheckCircle className="w-3 h-3 mr-1" />
                                        Ativo
                                      </Badge>
                                    ) : isExpired ? (
                                      <Badge className="bg-amber-50 text-amber-700 border-amber-200">
                                        <Clock className="w-3 h-3 mr-1" />
                                        Expirado
                                      </Badge>
                                    ) : (
                                      <Badge className="bg-red-50 text-red-700 border-red-200">
                                        <XCircle className="w-3 h-3 mr-1" />
                                        Revogado
                                      </Badge>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Badge variant="outline" className="text-xs capitalize">
                                      {consent.validation_method || 'manual'}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="text-sm text-slate-500">
                                    {new Date(consent.created_at).toLocaleDateString('pt-PT')}
                                  </TableCell>
                                  <TableCell className="text-sm">
                                    {consent.expiration_date ? (
                                      <span className={isExpired ? 'text-red-600 font-medium' : isExpiringSoon ? 'text-amber-600 font-medium' : 'text-slate-500'}>
                                        {new Date(consent.expiration_date).toLocaleDateString('pt-PT')}
                                        {consent.renewal_count > 0 && (
                                          <span className="text-xs ml-1 text-blue-500">
                                            (R{consent.renewal_count})
                                          </span>
                                        )}
                                      </span>
                                    ) : (
                                      <span className="text-slate-400">-</span>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <div className="flex items-center justify-end gap-1">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleViewEvidence(consent.consent_id)}
                                        title="Ver Evidência"
                                        data-testid={`evidence-consent-${consent.consent_id}`}
                                      >
                                        <Eye className="w-4 h-4 text-blue-500" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleEditConsent(titular, consent)}
                                        data-testid={`edit-consent-${consent.consent_id}`}
                                      >
                                        <Edit className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Titular Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ open, titular: open ? editDialog.titular : null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar Titular</DialogTitle>
            <DialogDescription>Atualizar informações do titular dos dados</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div>
              <Label htmlFor="edit-name">Nome</Label>
              <Input
                id="edit-name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit-phone">Telefone</Label>
              <Input
                id="edit-phone"
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                placeholder="+351 912 345 678"
              />
            </div>
            <div>
              <Label htmlFor="edit-address">Morada</Label>
              <Input
                id="edit-address"
                value={editForm.address}
                onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                placeholder="Rua, Cidade, Código Postal"
              />
            </div>
            <div>
              <Label className="mb-2 block">Grupos</Label>
              <div className="border rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto bg-slate-50">
                {groups.length === 0 ? (
                  <p className="text-sm text-slate-500">Nenhum grupo disponível</p>
                ) : (
                  groups.map(group => (
                    <div key={group.group_id} className="flex items-center gap-2">
                      <Checkbox
                        id={`edit-group-${group.group_id}`}
                        checked={editForm.groups?.includes(group.group_id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setEditForm({
                              ...editForm,
                              groups: [...(editForm.groups || []), group.group_id]
                            });
                          } else {
                            setEditForm({
                              ...editForm,
                              groups: (editForm.groups || []).filter(id => id !== group.group_id)
                            });
                          }
                        }}
                      />
                      <Label htmlFor={`edit-group-${group.group_id}`} className="flex items-center gap-2 cursor-pointer">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: group.color || '#3B82F6' }} />
                        <span>{group.name}</span>
                        <Badge variant="secondary" className="text-xs">{group.titular_count || 0}</Badge>
                      </Label>
                    </div>
                  ))
                )}
              </div>
              {editForm.groups?.length > 0 && (
                <p className="text-xs text-slate-500 mt-1">{editForm.groups.length} grupo(s) selecionado(s)</p>
              )}
            </div>

            {/* Base Legal */}
            <div>
              <Label>Base Legal (RGPD Art. 6º)</Label>
              <Select 
                value={editForm.legal_basis || 'consent'} 
                onValueChange={(v) => setEditForm({ ...editForm, legal_basis: v })}
              >
                <SelectTrigger className="mt-1" data-testid="edit-titular-legal-basis">
                  <SelectValue placeholder="Selecione a base legal" />
                </SelectTrigger>
                <SelectContent>
                  {legalBasisOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {editForm.legal_basis === 'consent' && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2 text-[#10B981] border-[#10B981]"
                  onClick={() => {
                    setEditDialog({ open: false, titular: null });
                    setUnifiedConsentModal({ 
                      open: true, 
                      titular: editDialog.titular
                    });
                  }}
                >
                  <QrCode className="w-4 h-4 mr-2" />
                  Recolher Consentimento
                </Button>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, titular: null })}>
              Cancelar
            </Button>
            <Button onClick={handleSaveTitular} className="bg-[#10B981] hover:bg-[#10B981]/90">
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Consent Dialog */}
      <Dialog open={consentDialog.open} onOpenChange={(open) => setConsentDialog({ open, titularId: open ? consentDialog.titularId : null, titularEmail: open ? consentDialog.titularEmail : null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Adicionar Consentimento</DialogTitle>
            <DialogDescription>Registar novo consentimento para o titular</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Tipo de Contacto *</Label>
              <Select value={consentForm.contact_type} onValueChange={(v) => setConsentForm({ ...consentForm, contact_type: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  {CONTACT_TYPES.map(ct => (
                    <SelectItem key={ct.id} value={ct.id}>{ct.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Valor do Contacto *</Label>
              <Input
                value={consentForm.contact_value}
                onChange={(e) => setConsentForm({ ...consentForm, contact_value: e.target.value })}
                placeholder={consentForm.contact_type === 'email' ? 'exemplo@email.com' : '+351 912 345 678'}
              />
            </div>
            <div>
              <Label>Método de Validação</Label>
              <Select 
                value={consentForm.validation_method} 
                onValueChange={(v) => setConsentForm({ ...consentForm, validation_method: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="otp">OTP (Email)</SelectItem>
                  <SelectItem value="qr">QR Code</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Prazo de Conservação</Label>
              <Select 
                value={consentForm.retention_period_id || 'consent_1_year'} 
                onValueChange={(v) => {
                  const period = RETENTION_PERIODS.find(p => p.id === v);
                  setConsentForm({ 
                    ...consentForm, 
                    retention_period_id: v,
                    retention_period_days: period?.value 
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RETENTION_PERIODS.map(rp => (
                    <SelectItem key={rp.id} value={rp.id}>
                      <div className="flex flex-col">
                        <span>{rp.label}</span>
                        <span className="text-xs text-slate-500">{rp.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Finalidades *</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {consentPurposes.map(p => (
                  <div key={p.purpose_id} className="flex items-center gap-2">
                    <Checkbox
                      id={`new-purpose-${p.purpose_id}`}
                      checked={consentForm.purposes.includes(p.purpose_id)}
                      onCheckedChange={() => togglePurpose(p.purpose_id, consentForm, setConsentForm)}
                    />
                    <label htmlFor={`new-purpose-${p.purpose_id}`} className="text-sm cursor-pointer">{p.name}</label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConsentDialog({ open: false, titularId: null, titularEmail: null })}>
              Cancelar
            </Button>
            <Button onClick={handleSaveNewConsent} className="bg-[#10B981] hover:bg-[#10B981]/90">
              Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Consent Collection Dialog (QR/OTP) */}
      <Dialog open={collectDialog.open} onOpenChange={(open) => {
        if (!open) {
          setCollectDialog({ open: false, titular: null });
          setQrImageUrl(null);
          setOtpSent(false);
          setOtpCode('');
          setValidationToken('');
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5 text-emerald-600" />
              Recolha de Consentimento
            </DialogTitle>
            <DialogDescription>
              Recolher consentimento de <strong>{collectDialog.titular?.name}</strong> via QR Code ou OTP
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="qr" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="qr" className="flex items-center gap-2">
                <QrCode className="w-4 h-4" />
                QR Code
              </TabsTrigger>
              <TabsTrigger value="otp" className="flex items-center gap-2">
                <Key className="w-4 h-4" />
                Código OTP
              </TabsTrigger>
            </TabsList>

            <TabsContent value="qr" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <p className="text-sm text-slate-600">
                    Gere um QR Code para o titular digitalizar e confirmar a sua identidade.
                  </p>
                  <Button onClick={handleGenerateQR} className="w-full bg-[#10B981] hover:bg-[#10B981]/90">
                    <QrCode className="w-4 h-4 mr-2" />
                    Gerar QR Code
                  </Button>
                </div>
                <div className="flex items-center justify-center p-4 bg-slate-50 rounded-lg min-h-[200px]">
                  {qrImageUrl ? (
                    <div className="text-center">
                      <img src={qrImageUrl} alt="QR Code" className="w-48 h-48 mx-auto" />
                      <p className="text-xs text-emerald-600 mt-2">✓ QR Code gerado</p>
                    </div>
                  ) : (
                    <div className="text-center text-slate-400">
                      <QrCode className="w-16 h-16 mx-auto mb-2" />
                      <p className="text-sm">QR Code aparecerá aqui</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="otp" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <p className="text-sm text-slate-600">
                    Envie um código OTP para o email do titular: <strong>{collectDialog.titular?.email}</strong>
                  </p>
                  {!otpSent ? (
                    <Button onClick={handleSendOTP} className="w-full bg-[#3B82F6] hover:bg-[#3B82F6]/90">
                      <Send className="w-4 h-4 mr-2" />
                      Enviar Código OTP
                    </Button>
                  ) : (
                    <div className="space-y-3">
                      <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                        <p className="text-sm text-emerald-700">✓ Código enviado para {collectDialog.titular?.email}</p>
                      </div>
                      <div>
                        <Label>Código OTP</Label>
                        <Input
                          value={otpCode}
                          onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="000000"
                          className="text-center text-xl tracking-widest font-mono"
                          maxLength={6}
                        />
                      </div>
                      <Button onClick={handleVerifyOTP} className="w-full" disabled={otpCode.length !== 6}>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Validar Código
                      </Button>
                      <Button variant="outline" onClick={handleSendOTP} className="w-full">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Reenviar Código
                      </Button>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-center p-4 bg-slate-50 rounded-lg min-h-[200px]">
                  {validationToken ? (
                    <div className="text-center">
                      <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-2" />
                      <p className="text-emerald-600 font-semibold">Identidade Verificada!</p>
                    </div>
                  ) : (
                    <div className="text-center text-slate-400">
                      <Key className="w-16 h-16 mx-auto mb-2" />
                      <p className="text-sm">Aguardando validação</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Consent Form */}
          <div className="border-t pt-4 mt-4">
            <h4 className="font-semibold text-[#0F172A] mb-3">Dados do Consentimento</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo de Contacto *</Label>
                <Select value={collectForm.contact_type} onValueChange={(v) => setCollectForm({ ...collectForm, contact_type: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTACT_TYPES.map(ct => (
                      <SelectItem key={ct.id} value={ct.id}>{ct.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Valor do Contacto *</Label>
                <Input
                  value={collectForm.contact_value}
                  onChange={(e) => setCollectForm({ ...collectForm, contact_value: e.target.value })}
                  placeholder={collectForm.contact_type === 'email' ? 'email@exemplo.com' : '+351...'}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <Label>Prazo de Conservação</Label>
                <Select 
                  value={collectForm.retention_period_id || 'consent_1_year'} 
                  onValueChange={(v) => {
                    const period = RETENTION_PERIODS.find(p => p.id === v);
                    setCollectForm({ 
                      ...collectForm, 
                      retention_period_id: v,
                      retention_period_days: period?.value 
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RETENTION_PERIODS.map(rp => (
                      <SelectItem key={rp.id} value={rp.id}>
                        <div className="flex flex-col">
                          <span>{rp.label}</span>
                          <span className="text-xs text-slate-500">{rp.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <p className="text-xs text-slate-500">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  {collectForm.retention_period_days 
                    ? `Expira em: ${new Date(Date.now() + collectForm.retention_period_days * 24 * 60 * 60 * 1000).toLocaleDateString('pt-PT')}`
                    : 'Sem data de expiração'
                  }
                </p>
              </div>
            </div>
            <div className="mt-4">
              <Label>Finalidades *</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {consentPurposes.map(p => (
                  <div key={p.purpose_id} className="flex items-center gap-2">
                    <Checkbox
                      id={`collect-purpose-${p.purpose_id}`}
                      checked={collectForm.purposes.includes(p.purpose_id)}
                      onCheckedChange={() => togglePurpose(p.purpose_id, collectForm, setCollectForm)}
                    />
                    <label htmlFor={`collect-purpose-${p.purpose_id}`} className="text-sm cursor-pointer">{p.name}</label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCollectDialog({ open: false, titular: null })}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSaveCollectedConsent} 
              className="bg-[#10B981] hover:bg-[#10B981]/90"
              disabled={!qrImageUrl && !validationToken}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Guardar Consentimento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Consent Dialog */}
      <Dialog open={editConsentDialog.open} onOpenChange={(open) => setEditConsentDialog({ open, titular: open ? editConsentDialog.titular : null, consent: open ? editConsentDialog.consent : null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Consentimento</DialogTitle>
            <DialogDescription>Atualizar informações do consentimento</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Valor do Contacto</Label>
              <Input
                value={editConsentForm.contact_value}
                onChange={(e) => setEditConsentForm({ ...editConsentForm, contact_value: e.target.value })}
              />
            </div>
            <div>
              <Label>Estado</Label>
              <Select value={editConsentForm.status} onValueChange={(v) => setEditConsentForm({ ...editConsentForm, status: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Ativo</SelectItem>
                  <SelectItem value="revoked">Revogado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Finalidades</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {consentPurposes.map(p => (
                  <div key={p.purpose_id} className="flex items-center gap-2">
                    <Checkbox
                      id={`edit-purpose-${p.purpose_id}`}
                      checked={editConsentForm.purposes?.includes(p.purpose_id)}
                      onCheckedChange={() => togglePurpose(p.purpose_id, editConsentForm, setEditConsentForm)}
                    />
                    <label htmlFor={`edit-purpose-${p.purpose_id}`} className="text-sm cursor-pointer">{p.name}</label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditConsentDialog({ open: false, titular: null, consent: null })}>
              Cancelar
            </Button>
            <Button onClick={handleSaveConsent} className="bg-[#10B981] hover:bg-[#10B981]/90">
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Titular Dialog */}
      <Dialog open={createTitularDialog} onOpenChange={setCreateTitularDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#10B981]" />
              Novo Titular dos Dados
            </DialogTitle>
            <DialogDescription>Registar um novo titular de dados pessoais</DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {/* Identificação Pessoal */}
            <div className="space-y-4">
              <h4 className="font-medium text-sm text-gray-700 border-b pb-2">Identificação Pessoal</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="new-first-name">Primeiro Nome *</Label>
                  <Input
                    id="new-first-name"
                    value={newTitularForm.first_name}
                    onChange={(e) => setNewTitularForm({ ...newTitularForm, first_name: e.target.value })}
                    placeholder="João"
                    data-testid="new-titular-first-name"
                  />
                </div>
                <div>
                  <Label htmlFor="new-last-name">Sobrenome</Label>
                  <Input
                    id="new-last-name"
                    value={newTitularForm.last_name}
                    onChange={(e) => setNewTitularForm({ ...newTitularForm, last_name: e.target.value })}
                    placeholder="Silva"
                    data-testid="new-titular-last-name"
                  />
                </div>
                <div>
                  <Label htmlFor="new-surname">Apelido</Label>
                  <Input
                    id="new-surname"
                    value={newTitularForm.surname}
                    onChange={(e) => setNewTitularForm({ ...newTitularForm, surname: e.target.value })}
                    placeholder="Santos"
                    data-testid="new-titular-surname"
                  />
                </div>
              </div>
            </div>

            {/* Contactos */}
            <div className="space-y-4">
              <h4 className="font-medium text-sm text-gray-700 border-b pb-2">Contactos</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="new-email">Email *</Label>
                  <Input
                    id="new-email"
                    type="email"
                    value={newTitularForm.email}
                    onChange={(e) => setNewTitularForm({ ...newTitularForm, email: e.target.value })}
                    placeholder="email@exemplo.com"
                    data-testid="new-titular-email"
                  />
                </div>
                <div>
                  <Label htmlFor="new-phone">Telefone</Label>
                  <Input
                    id="new-phone"
                    value={newTitularForm.phone}
                    onChange={(e) => setNewTitularForm({ ...newTitularForm, phone: e.target.value })}
                    placeholder="+351 21 000 0000"
                    data-testid="new-titular-phone"
                  />
                </div>
                <div>
                  <Label htmlFor="new-mobile">Telemóvel</Label>
                  <Input
                    id="new-mobile"
                    value={newTitularForm.mobile}
                    onChange={(e) => setNewTitularForm({ ...newTitularForm, mobile: e.target.value })}
                    placeholder="+351 912 345 678"
                    data-testid="new-titular-mobile"
                  />
                </div>
              </div>
            </div>

            {/* Dados Empresariais */}
            <div className="space-y-4">
              <h4 className="font-medium text-sm text-gray-700 border-b pb-2">Dados Empresariais</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="new-company">Nome da Empresa</Label>
                  <Input
                    id="new-company"
                    value={newTitularForm.company_name}
                    onChange={(e) => setNewTitularForm({ ...newTitularForm, company_name: e.target.value })}
                    placeholder="Empresa Lda"
                    data-testid="new-titular-company"
                  />
                </div>
                <div>
                  <Label htmlFor="new-nif">NIF</Label>
                  <Input
                    id="new-nif"
                    value={newTitularForm.nif}
                    onChange={(e) => setNewTitularForm({ ...newTitularForm, nif: e.target.value })}
                    placeholder="123456789"
                    data-testid="new-titular-nif"
                  />
                </div>
                <div>
                  <Label htmlFor="new-autoline">ID Autoline</Label>
                  <Input
                    id="new-autoline"
                    value={newTitularForm.autoline_id}
                    onChange={(e) => setNewTitularForm({ ...newTitularForm, autoline_id: e.target.value })}
                    placeholder="AUTO-12345"
                    data-testid="new-titular-autoline"
                  />
                </div>
              </div>
            </div>

            {/* Morada */}
            <div className="space-y-4">
              <h4 className="font-medium text-sm text-gray-700 border-b pb-2">Morada</h4>
              <div>
                <Label htmlFor="new-address">Morada</Label>
                <Input
                  id="new-address"
                  value={newTitularForm.address}
                  onChange={(e) => setNewTitularForm({ ...newTitularForm, address: e.target.value })}
                  placeholder="Rua Exemplo, Nº 123, 1º Dto"
                  data-testid="new-titular-address"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="new-locality">Localidade</Label>
                  <Input
                    id="new-locality"
                    value={newTitularForm.locality}
                    onChange={(e) => setNewTitularForm({ ...newTitularForm, locality: e.target.value })}
                    placeholder="Lisboa"
                    data-testid="new-titular-locality"
                  />
                </div>
                <div>
                  <Label htmlFor="new-postal-code">Código Postal</Label>
                  <Input
                    id="new-postal-code"
                    value={newTitularForm.postal_code}
                    onChange={(e) => setNewTitularForm({ ...newTitularForm, postal_code: e.target.value })}
                    placeholder="1000-001"
                    data-testid="new-titular-postal-code"
                  />
                </div>
              </div>
            </div>

            {/* Base Legal */}
            <div className="space-y-4">
              <h4 className="font-medium text-sm text-gray-700 border-b pb-2">Base Legal (RGPD Art. 6º) *</h4>
              <div>
                <Label htmlFor="new-legal-basis">Fundamento para Tratamento de Dados</Label>
                <Select 
                  value={newTitularForm.legal_basis} 
                  onValueChange={(v) => setNewTitularForm({ ...newTitularForm, legal_basis: v })}
                >
                  <SelectTrigger className="mt-1" data-testid="new-titular-legal-basis">
                    <SelectValue placeholder="Selecione a base legal" />
                  </SelectTrigger>
                  <SelectContent>
                    {legalBasisOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  {newTitularForm.legal_basis === 'consent' 
                    ? 'Será aberta a janela de recolha de consentimento após criar o titular.' 
                    : 'Não será necessário recolher consentimento explícito.'}
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateTitularDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCreateTitular} 
              className="bg-[#10B981] hover:bg-[#10B981]/90"
              data-testid="save-new-titular"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Criar Titular
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RTBF Dialog - Right To Be Forgotten */}
      <Dialog open={rtbfDialog.open} onOpenChange={(open) => setRtbfDialog({ open, titular: open ? rtbfDialog.titular : null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Direito ao Esquecimento (Art. 17º RGPD)
            </DialogTitle>
            <DialogDescription>
              Executar o direito ao apagamento dos dados de <strong>{rtbfDialog.titular?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <h4 className="font-semibold text-amber-800 mb-2">Aviso Importante</h4>
              <p className="text-sm text-amber-700">
                Esta ação irá apagar permanentemente todos os dados pessoais e consentimentos deste titular. 
                Esta operação é irreversível e deve cumprir com o Artigo 17º do RGPD.
              </p>
            </div>

            <div>
              <Label>Tipo de Execução *</Label>
              <Select 
                value={rtbfForm.execution_type} 
                onValueChange={(v) => setRtbfForm({ ...rtbfForm, execution_type: v })}
              >
                <SelectTrigger data-testid="rtbf-execution-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="immediate">
                    <div className="flex items-center gap-2">
                      <Trash2 className="w-4 h-4 text-red-500" />
                      Imediato - Apagar agora
                    </div>
                  </SelectItem>
                  <SelectItem value="delayed">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-amber-500" />
                      Diferido - Apagar em 20 dias
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                {rtbfForm.execution_type === 'delayed' 
                  ? 'Os dados serão apagados após 20 dias (período de reflexão)' 
                  : 'Os dados serão apagados imediatamente'}
              </p>
            </div>

            <div>
              <Label htmlFor="rtbf-reason">Motivo</Label>
              <Input
                id="rtbf-reason"
                value={rtbfForm.reason}
                onChange={(e) => setRtbfForm({ ...rtbfForm, reason: e.target.value })}
                placeholder="Motivo do pedido de apagamento"
              />
            </div>

            <div>
              <Label htmlFor="rtbf-notes">Notas Adicionais</Label>
              <Input
                id="rtbf-notes"
                value={rtbfForm.notes}
                onChange={(e) => setRtbfForm({ ...rtbfForm, notes: e.target.value })}
                placeholder="Observações relevantes"
              />
            </div>

            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="identity-verified"
                  checked={rtbfForm.identity_verified}
                  onCheckedChange={(checked) => setRtbfForm({ ...rtbfForm, identity_verified: checked })}
                  data-testid="rtbf-identity-checkbox"
                />
                <label htmlFor="identity-verified" className="text-sm text-red-800 cursor-pointer">
                  <strong>Confirmo que foram cumpridos todos os mecanismos de validação da identidade</strong> do titular dos dados que exerceu este direito, conforme exigido pelo Artigo 12º do RGPD.
                </label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRtbfDialog({ open: false, titular: null })}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSubmitRTBF}
              disabled={!rtbfForm.identity_verified}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="submit-rtbf"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {rtbfForm.execution_type === 'immediate' ? 'Apagar Agora' : 'Agendar Apagamento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Evidence Dialog */}
      <Dialog open={evidenceDialog.open} onOpenChange={(open) => setEvidenceDialog({ open, evidence: open ? evidenceDialog.evidence : null, loading: false })}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-500" />
              Evidência de Consentimento
            </DialogTitle>
            <DialogDescription>
              Prova documental para conformidade RGPD
            </DialogDescription>
          </DialogHeader>
          
          {evidenceDialog.loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#10B981]"></div>
            </div>
          ) : evidenceDialog.evidence ? (
            <div className="space-y-4">
              {/* Consent ID */}
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500">ID do Consentimento</p>
                <p className="font-mono text-sm">{evidenceDialog.evidence.consent_id}</p>
              </div>
              
              {/* Titular Info */}
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold text-[#0F172A] mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Titular dos Dados
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-slate-500">Nome</p>
                    <p className="font-medium">{evidenceDialog.evidence.titular?.name}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Email</p>
                    <p className="font-medium">{evidenceDialog.evidence.titular?.email}</p>
                  </div>
                </div>
              </div>
              
              {/* Consent Details */}
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold text-[#0F172A] mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Detalhes do Consentimento
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-slate-500">Tipo de Contacto</p>
                    <p className="font-medium capitalize">{evidenceDialog.evidence.consent_details?.contact_type}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Valor</p>
                    <p className="font-medium">{evidenceDialog.evidence.consent_details?.contact_value}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Finalidades</p>
                    <div className="flex flex-wrap gap-1">
                      {evidenceDialog.evidence.consent_details?.purposes?.map(p => (
                        <Badge key={p} variant="outline" className="text-xs">{p}</Badge>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-slate-500">Estado</p>
                    <Badge className={evidenceDialog.evidence.consent_details?.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}>
                      {evidenceDialog.evidence.consent_details?.status === 'active' ? 'Ativo' : 'Revogado'}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-slate-500">Prazo de Conservação</p>
                    <p className="font-medium">{evidenceDialog.evidence.consent_details?.retention_period_name || `${evidenceDialog.evidence.consent_details?.retention_period_days} dias`}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Data de Expiração</p>
                    <p className="font-medium">
                      {evidenceDialog.evidence.consent_details?.expiration_date 
                        ? new Date(evidenceDialog.evidence.consent_details.expiration_date).toLocaleDateString('pt-PT')
                        : 'Sem prazo'}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Validation Evidence */}
              <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
                <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Evidência de Validação
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-blue-600">Método de Validação</p>
                    <Badge className="bg-blue-100 text-blue-800 uppercase">
                      {evidenceDialog.evidence.validation_evidence?.method || 'Manual'}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-blue-600">Data/Hora</p>
                    <p className="font-medium text-blue-900">
                      {evidenceDialog.evidence.validation_evidence?.timestamp 
                        ? new Date(evidenceDialog.evidence.validation_evidence.timestamp).toLocaleString('pt-PT')
                        : 'N/A'}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-blue-600 flex items-center gap-1">
                      <Globe className="w-3 h-3" />
                      Endereço IP
                    </p>
                    <p className="font-mono text-blue-900 bg-white px-2 py-1 rounded">
                      {evidenceDialog.evidence.validation_evidence?.ip_address || 'N/A'}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-blue-600 flex items-center gap-1">
                      <Monitor className="w-3 h-3" />
                      User Agent
                    </p>
                    <p className="font-mono text-xs text-blue-900 bg-white px-2 py-1 rounded break-all">
                      {evidenceDialog.evidence.validation_evidence?.user_agent || 'N/A'}
                    </p>
                  </div>
                </div>
                
                {/* Device Information - Detailed */}
                {evidenceDialog.evidence.validation_evidence?.device_info && (
                  <div className="mt-4 pt-4 border-t border-blue-200">
                    <h5 className="text-blue-700 font-medium mb-3 flex items-center gap-2">
                      <Laptop className="w-4 h-4" />
                      Informação Detalhada do Dispositivo
                    </h5>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div className="bg-white p-2 rounded border">
                        <p className="text-blue-600 flex items-center gap-1 text-xs mb-1">
                          <Chrome className="w-3 h-3" />
                          Browser
                        </p>
                        <p className="font-medium text-blue-900">
                          {evidenceDialog.evidence.validation_evidence.device_info?.browser?.full || 'N/A'}
                        </p>
                      </div>
                      <div className="bg-white p-2 rounded border">
                        <p className="text-blue-600 flex items-center gap-1 text-xs mb-1">
                          <Monitor className="w-3 h-3" />
                          Sistema Operativo
                        </p>
                        <p className="font-medium text-blue-900">
                          {evidenceDialog.evidence.validation_evidence.device_info?.os?.full || 'N/A'}
                        </p>
                      </div>
                      <div className="bg-white p-2 rounded border">
                        <p className="text-blue-600 flex items-center gap-1 text-xs mb-1">
                          {evidenceDialog.evidence.validation_evidence.device_info?.is_mobile ? (
                            <Smartphone className="w-3 h-3" />
                          ) : evidenceDialog.evidence.validation_evidence.device_info?.is_tablet ? (
                            <Tablet className="w-3 h-3" />
                          ) : (
                            <Laptop className="w-3 h-3" />
                          )}
                          Dispositivo
                        </p>
                        <p className="font-medium text-blue-900">
                          {evidenceDialog.evidence.validation_evidence.device_info?.device?.full || 
                           evidenceDialog.evidence.validation_evidence.device_info?.device?.type || 'N/A'}
                        </p>
                      </div>
                    </div>
                    <div className="mt-2 flex gap-2">
                      {evidenceDialog.evidence.validation_evidence.device_info?.is_mobile && (
                        <Badge variant="outline" className="text-xs">📱 Mobile</Badge>
                      )}
                      {evidenceDialog.evidence.validation_evidence.device_info?.is_tablet && (
                        <Badge variant="outline" className="text-xs">📱 Tablet</Badge>
                      )}
                      {evidenceDialog.evidence.validation_evidence.device_info?.is_pc && (
                        <Badge variant="outline" className="text-xs">💻 Desktop</Badge>
                      )}
                      {evidenceDialog.evidence.validation_evidence.device_info?.is_bot && (
                        <Badge variant="outline" className="text-xs bg-yellow-50">🤖 Bot</Badge>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Creation Evidence */}
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold text-[#0F172A] mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Evidência de Criação
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-slate-500">Data de Criação</p>
                    <p className="font-medium">
                      {evidenceDialog.evidence.creation_evidence?.created_at 
                        ? new Date(evidenceDialog.evidence.creation_evidence.created_at).toLocaleString('pt-PT')
                        : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500">Criado Por</p>
                    <p className="font-medium">
                      {evidenceDialog.evidence.creation_evidence?.creator_name || 'Sistema'}
                      {evidenceDialog.evidence.creation_evidence?.creator_role && (
                        <Badge variant="outline" className="ml-2 text-xs capitalize">
                          {evidenceDialog.evidence.creation_evidence.creator_role}
                        </Badge>
                      )}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Legal Basis */}
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <h4 className="font-semibold text-emerald-800 mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Base Legal
                </h4>
                <div className="text-sm text-emerald-800 space-y-1">
                  <p><strong>Fundamento:</strong> {evidenceDialog.evidence.legal_basis?.basis || 'Consentimento'}</p>
                  <p><strong>Artigo RGPD:</strong> {evidenceDialog.evidence.legal_basis?.gdpr_article || 'Art. 6º, nº 1, alínea a) do RGPD'}</p>
                  <p className="text-xs text-emerald-700 mt-2 italic">
                    {evidenceDialog.evidence.legal_basis?.description || 'O tratamento é lícito se o titular dos dados tiver dado o seu consentimento para o tratamento dos seus dados pessoais para uma ou mais finalidades específicas.'}
                  </p>
                </div>
              </div>
              
              {/* History */}
              {evidenceDialog.evidence.history && evidenceDialog.evidence.history.length > 0 && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold text-[#0F172A] mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Histórico de Alterações ({evidenceDialog.evidence.history.length})
                  </h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {evidenceDialog.evidence.history.map((item, index) => (
                      <div key={index} className="text-sm p-2 bg-slate-50 rounded border-l-2 border-slate-300">
                        <div className="flex justify-between items-center">
                          <Badge variant="outline" className="capitalize text-xs">
                            {item.action === 'created' ? 'Criado' : 
                             item.action === 'updated' ? 'Atualizado' : 
                             item.action === 'revoked' ? 'Revogado' : 
                             item.action === 'renewed' ? 'Renovado' : item.action}
                          </Badge>
                          <span className="text-xs text-slate-500">
                            {item.timestamp ? new Date(item.timestamp).toLocaleString('pt-PT') : 'N/A'}
                          </span>
                        </div>
                        {item.performed_by && (
                          <p className="text-xs text-slate-600 mt-1">
                            Por: {item.performed_by} {item.performed_by_role && `(${item.performed_by_role})`}
                          </p>
                        )}
                        {item.ip_address && (
                          <p className="text-xs text-slate-500">IP: {item.ip_address}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEvidenceDialog({ open: false, evidence: null, loading: false })}>
              Fechar
            </Button>
            <Button 
              className="bg-[#10B981] hover:bg-[#10B981]/90"
              onClick={async () => {
                if (!evidenceDialog.evidence?.consent_id) return;
                try {
                  const response = await axios.get(
                    `${BACKEND_URL}/api/consents/${evidenceDialog.evidence.consent_id}/evidence/pdf`,
                    { withCredentials: true, responseType: 'blob' }
                  );
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `evidencia_consentimento_${evidenceDialog.evidence.consent_id}.pdf`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  window.URL.revokeObjectURL(url);
                  toast.success('PDF exportado com sucesso!');
                } catch (error) {
                  console.error('Error exporting PDF:', error);
                  toast.error('Erro ao exportar PDF');
                }
              }}
            >
              <Download className="w-4 h-4 mr-2" />
              Exportar PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unified Consent Collection Modal */}
      <ConsentCollectionModal
        isOpen={unifiedConsentModal.open}
        onClose={() => setUnifiedConsentModal({ open: false, titular: null })}
        titularData={unifiedConsentModal.titular ? {
          user_id: unifiedConsentModal.titular.user_id,  // Important: Pass titular ID for consent association
          titular_id: unifiedConsentModal.titular.user_id, // Alias for QR generation
          contact_type: unifiedConsentModal.titular.contacts?.email?.length > 0 ? 'email' : 
                        unifiedConsentModal.titular.contacts?.phone?.length > 0 ? 'phone' : 'email',
          contact_value: unifiedConsentModal.titular.contacts?.email?.[0] || 
                         unifiedConsentModal.titular.contacts?.phone?.[0] || 
                         unifiedConsentModal.titular.email || '',
          email: unifiedConsentModal.titular.email,
          phone: unifiedConsentModal.titular.phone,
          mobile: unifiedConsentModal.titular.mobile,
          contacts: unifiedConsentModal.titular.contacts,
          purposes: unifiedConsentModal.titular.purposes || []
        } : null}
        onSuccess={handleUnifiedConsentSuccess}
        showForm={true}
      />
    </div>
  );
};

export default TitularesPage;
